import type { FC } from "react";
import { useEffect, useState } from "react";

import clsx from "clsx";

import { browserStatics } from "@/src/shared/BrowserStatic";
import type { IAccountFormat } from "@Components/Modules/Helpers";
import { accountNumberFormat, formatAmount } from "@Components/Modules/Helpers";
import type { ITextbox } from "@Components/Textbox";

import type { IButtonsBar } from "../common/ButtonsBar";
import { ButtonsBar } from "../common/ButtonsBar";
import type { INavigationModalActions } from "../common/NavigationModal";
import type { IInputTracker } from "../telemetry/InputTracker";
import { TextboxTracker } from "../telemetry/InputTracker";
import type { ICashbackClaimFields } from "./CashbackCommonInterfaces";

import * as css from "./CashbackClaimForm.module.css";

export interface ICashbackClaimForm extends INavigationModalActions<ICashbackClaimFields> {
    title: string;
    cashbackAmountLabel: string;
    cashbackAmountTextbox: IInputTracker<ITextbox>;
    buttonsBar: IButtonsBar;
}

interface ICashbackClaimFormState {
    cashbackNewAmount?: string;
    cashbackNewAmountValue?: number;
    disabledButton: boolean;
    timeout: number | null;
}

export const CashbackClaimForm: FC<ICashbackClaimForm> = (props) => {
    const { language } = browserStatics;

    const updateStateObj = (val: string, initialValue = false) => {
        const { amount, amountFormat, numberFormat }: IAccountFormat = accountNumberFormat(val, language, 5);

        const amountHydrated = amount || 0;

        return {
            cashbackNewAmount: initialValue ? amountFormat : numberFormat,
            cashbackNewAmountValue: amountHydrated,
            disabledButton: !amountHydrated,
            amountFormat,
        };
    };

    const [state, setState] = useState<ICashbackClaimFormState>({
        ...updateStateObj(props.fieldValues?.cashbackNewAmount, true),
        timeout: null,
    });

    useEffect(() => (state.timeout ? clearTimeout(state.timeout) : undefined), []);

    const onChangeAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (state.timeout) {
            clearTimeout(state.timeout);
        }

        const values = updateStateObj(event.target.value);

        setState({
            ...updateStateObj(event.target.value),
            timeout: window.setTimeout(
                () =>
                    setState((currState) => ({
                        ...currState,
                        cashbackNewAmount: values.amountFormat,
                        timeout: null,
                    })),
                1500,
            ),
        });

        return false;
    };

    const previousPage = () => {
        if (props.previousPage && props.saveValues) {
            props.saveValues({ cashbackNewAmount: "" });
            props.previousPage();
        }
    };

    const submitForm = () => {
        if (props.saveValues && props.onSubmit) {
            props.saveValues({
                cashbackNewAmount: formatAmount(state.cashbackNewAmountValue, language),
                cashbackNewAmountValue: state.cashbackNewAmountValue,
            });
            props.onSubmit();
        }
    };

    const cashbackAmountTextbox: IInputTracker<ITextbox> = {
        ...props.cashbackAmountTextbox,
        clientId: props.cashbackAmountTextbox.clientId || "cashback-amount-input",
        focusTelemetryCustom: { eventName: "cshbck-clmMakeAmountEdit", actionType: "Undefined" },
        properties: {
            ...props.cashbackAmountTextbox.properties,
            maxLength: 8,
            onChange: onChangeAmount,
            value: state.cashbackNewAmount,
            preventPaste: true,
        },
    };

    const buttonsBar: IButtonsBar = props.buttonsBar && {
        ...props.buttonsBar,
        nextButton: {
            ...props.buttonsBar.nextButton,
            disabled: state.disabledButton || state.cashbackNewAmountValue == null,
            onClick: submitForm,
        },
        cancelButton: {
            ...props.buttonsBar.cancelButton,
            onClick: previousPage,
        },
    };

    return (
        <div className={clsx(css.cashbackClaimFormComponent, css.standardModalForm)}>
            <h1 className={css.standardModalTitle}>{props.title}</h1>
            <div className={css.standardModalFormBody}>
                <div className={css.inputNewValue}>
                    <label className={css.standardModalLabel} htmlFor={cashbackAmountTextbox.clientId}>
                        {props.cashbackAmountLabel}
                    </label>
                    <TextboxTracker {...cashbackAmountTextbox} />
                </div>
            </div>
            <ButtonsBar {...buttonsBar} />
        </div>
    );
};
